import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ComposedChart, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Calendar as CalendarIcon, MessageSquare, Link as LinkIcon, Copy, Check, Edit3, Save, X, StickyNote, AlertTriangle, RefreshCw, LogIn, LogOut, Home, User, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Obtener mes en curso y año correspondiente por defecto
const getInfoMesActual = () => {
  const d = new Date();
  return {
    mes: String(d.getMonth() + 1).padStart(2, '0'),
    year: d.getFullYear()
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { propiedadSeleccionada, propiedades, cambiarPropiedad } = usePropiedad();
  const infoMesActual = useMemo(() => getInfoMesActual(), []);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(infoMesActual.year);
  const [proximasReservas, setProximasReservas] = useState([]);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [editandoNota, setEditandoNota] = useState(null); // ID de reserva siendo editada
  const [notaTemporal, setNotaTemporal] = useState('');
  const [gastosPendientes, setGastosPendientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [montoEditando, setMontoEditando] = useState({});
  const [checkInsHoy, setCheckInsHoy] = useState([]);
  const [checkOutsHoy, setCheckOutsHoy] = useState([]);
  const [huespedesActuales, setHuespedesActuales] = useState([]);
  const [mesGastosCategoria, setMesGastosCategoria] = useState(infoMesActual.mes); // Por default mes en curso ('01', '02', etc.) o 'todos'
  const [mostrarResumenGastos, setMostrarResumenGastos] = useState(false);
  const [vistaGastosGrafico, setVistaGastosGrafico] = useState('apilado'); // 'apilado' | 'separado'
  const [periodoEjercicio, setPeriodoEjercicio] = useState('mes_actual'); // 'mes_actual' | 'mes_anterior' | 'anio' | '01'..'12'

  // URL de la Fan Page del departamento
  const fanPageUrl = propiedadSeleccionada?.id_propiedad ? `${window.location.origin}/propiedad/${propiedadSeleccionada.id_propiedad}` : '';

  useEffect(() => {
    cargarSolicitudesPendientes();
  }, []);

  const cargarEstadisticas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/stats`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad, year }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [propiedadSeleccionada?.id_propiedad, year]);

  const cargarSolicitudesPendientes = async () => {
    try {
      const response = await axios.get(`${API}/solicitudes-reserva`);
      const pendientes = response.data.filter(s => s.estado === 'pendiente');
      setSolicitudesPendientes(pendientes);
    } catch (error) {
      console.error('Error al cargar solicitudes pendientes:', error);
    }
  };

  const cargarGastosPendientes = useCallback(async () => {
    try {
      const [resGastos, resCategorias] = await Promise.all([
        axios.get(`${API}/gastos-pendientes-proximos`, {
          params: { propiedad_id: propiedadSeleccionada?.id_propiedad, dias: 5 }
        }),
        axios.get(`${API}/categorias`)
      ]);
      setGastosPendientes(resGastos.data);
      setCategorias(resCategorias.data);
    } catch (error) {
      console.error('Error al cargar gastos pendientes:', error);
    }
  }, [propiedadSeleccionada?.id_propiedad]);

  const cargarCategorias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categorias`);
      setCategorias(res.data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  }, []);

  const marcarGastoPagado = async (gasto) => {
    try {
      const montoFinal = montoEditando[gasto.id_gasto] !== undefined 
        ? parseFloat(montoEditando[gasto.id_gasto]) 
        : gasto.monto;
      
      await axios.patch(`${API}/gastos/${gasto.id_gasto}/pagar`, null, {
        params: { monto: montoFinal }
      });
      toast.success('Gasto marcado como pagado');
      cargarGastosPendientes();
      cargarEstadisticas(); // Actualizar estadísticas
    } catch (error) {
      toast.error('Error al marcar gasto como pagado');
    }
  };

  const obtenerNombreCategoria = (categoriaId) => {
    const cat = categorias.find(c => c.id_categoria === categoriaId);
    return cat?.nombre || 'Sin categoría';
  };

  const cargarProximasReservas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reservas`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad }
      });
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const hoyStr = hoy.toISOString().split('T')[0];
      const manana = new Date(hoy);
      manana.setDate(manana.getDate() + 1);
      const mananaStr = manana.toISOString().split('T')[0];
      
      // Filtrar reservas futuras (excluyendo Cancelada, Bloqueada, Completada), tomar las 2 primeras
      const estadosExcluidos = ['Cancelada', 'Bloqueada', 'Completada', 'Expirada'];
      const reservasFuturas = response.data
        .filter(r => {
          const checkIn = new Date(r.fecha_check_in + 'T12:00:00');
          const estadoActual = r.estado_reserva || '';
          return checkIn >= hoy && !estadosExcluidos.some(excluido => estadoActual.toLowerCase().includes(excluido.toLowerCase()));
        })
        .sort((a, b) => new Date(a.fecha_check_in + 'T12:00:00') - new Date(b.fecha_check_in + 'T12:00:00'))
        .slice(0, 2);
      
      setProximasReservas(reservasFuturas);

      // Check-ins de hoy y mañana
      const checkIns = response.data.filter(r => {
        const estadoActual = r.estado_reserva || '';
        const esEstadoValido = !estadosExcluidos.some(excluido => estadoActual.toLowerCase().includes(excluido.toLowerCase()));
        return esEstadoValido && (r.fecha_check_in === hoyStr || r.fecha_check_in === mananaStr);
      });
      setCheckInsHoy(checkIns);

      // Check-outs de hoy y mañana
      const checkOuts = response.data.filter(r => {
        const estadoActual = r.estado_reserva || '';
        const esEstadoValido = !['Cancelada', 'Bloqueada', 'Expirada'].some(excluido => estadoActual.toLowerCase().includes(excluido.toLowerCase()));
        return esEstadoValido && (r.fecha_check_out === hoyStr || r.fecha_check_out === mananaStr);
      });
      setCheckOutsHoy(checkOuts);

      // Huéspedes actuales (estado DISFRUTANDO o reservas activas entre check-in y check-out)
      const huespedes = response.data.filter(r => {
        const estadoActual = (r.estado_reserva || '').toLowerCase();
        if (estadoActual.includes('disfrutando')) return true;
        
        // También verificar por fechas si el huésped está actualmente hospedado
        const checkIn = new Date(r.fecha_check_in + 'T00:00:00');
        const checkOut = new Date(r.fecha_check_out + 'T23:59:59');
        const ahora = new Date();
        const esActivo = checkIn <= ahora && checkOut >= ahora;
        const esEstadoValido = !estadosExcluidos.some(excluido => estadoActual.includes(excluido.toLowerCase()));
        return esActivo && esEstadoValido;
      });
      setHuespedesActuales(huespedes);
      
    } catch (error) {
      console.error('Error al cargar próximas reservas:', error);
    }
  }, [propiedadSeleccionada?.id_propiedad]);
  useEffect(() => {
    cargarCategorias();
    if (propiedadSeleccionada?.id_propiedad) {
      cargarEstadisticas();
      cargarProximasReservas();
      cargarGastosPendientes();
    }
  }, [year, propiedadSeleccionada?.id_propiedad, cargarEstadisticas, cargarProximasReservas, cargarGastosPendientes, cargarCategorias]);

  const enviarWhatsAppCliente = async (e, reserva) => {
    e.stopPropagation(); // Evitar que se active el onClick del contenedor
    
    if (!reserva) return;
    
    try {
      // Buscar el cliente por nombre en la propiedad actual
      const responseClientes = await axios.get(`${API}/clientes`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad }
      });
      
      // Intentar buscar por cliente_id si está disponible
      let cliente = null;
      if (reserva.cliente_id) {
        cliente = responseClientes.data.find(c => c.id_cliente === reserva.cliente_id);
      }
      
      // Si no se encontró por ID, buscar por nombre
      if (!cliente) {
        const nombreReserva = reserva.huesped_nombre.toLowerCase().trim();
        cliente = responseClientes.data.find(c => {
          const nombreCompleto = `${c.nombre} ${c.apellido || ''}`.toLowerCase().trim();
          const soloNombre = c.nombre.toLowerCase().trim();
          return nombreCompleto === nombreReserva || soloNombre === nombreReserva;
        });
      }
      
      if (!cliente) {
        toast.error(`Cliente "${reserva.huesped_nombre}" no encontrado en esta propiedad`);
        return;
      }
      
      if (!cliente.telefono || cliente.telefono.trim() === '') {
        toast.error('Este cliente no tiene teléfono registrado');
        return;
      }
      
      // Redirigir a la página de mensajes con el cliente pre-seleccionado
      const nombreCompleto = `${cliente.nombre}${cliente.apellido ? ' ' + cliente.apellido : ''}`;
      navigate('/mensajes-whatsapp', { 
        state: { 
          clientePreseleccionado: cliente.id_cliente,
          nombreCliente: nombreCompleto 
        } 
      });
    } catch (error) {
      toast.error('Error al cargar información del cliente');
      console.error(error);
    }
  };

  // Funciones para editar nota de reserva
  const iniciarEdicionNota = (e, reserva) => {
    e.stopPropagation();
    setEditandoNota(reserva.id_reserva);
    setNotaTemporal(reserva.notas || '');
  };

  const cancelarEdicionNota = (e) => {
    e.stopPropagation();
    setEditandoNota(null);
    setNotaTemporal('');
  };

  const guardarNota = async (e, reservaId) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/reservas/${reservaId}/notas`, null, {
        params: { notas: notaTemporal }
      });
      
      // Actualizar el estado local
      setProximasReservas(prev => prev.map(r => 
        r.id_reserva === reservaId ? { ...r, notas: notaTemporal } : r
      ));
      
      setEditandoNota(null);
      setNotaTemporal('');
      toast.success('Nota guardada');
    } catch (error) {
      toast.error('Error al guardar la nota');
      console.error(error);
    }
  };

  const copiarLinkFanPage = async () => {
    if (!fanPageUrl) {
      toast.error('Selecciona una propiedad primero');
      return;
    }
    
    try {
      // Usar API moderna de clipboard
      await navigator.clipboard.writeText(fanPageUrl);
      
      setLinkCopiado(true);
      toast.success('¡Link de Fan Page copiado al portapapeles!');
      
      // Resetear el estado después de 2 segundos
      setTimeout(() => {
        setLinkCopiado(false);
      }, 2000);
    } catch (error) {
      console.error('Error al copiar:', error);
      
      // Fallback: método alternativo
      try {
        const textArea = document.createElement('textarea');
        textArea.value = fanPageUrl;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setLinkCopiado(true);
          toast.success('¡Link copiado al portapapeles!');
          setTimeout(() => setLinkCopiado(false), 2000);
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        console.error('Fallback también falló:', fallbackError);
        toast.error('No se pudo copiar. Copia manualmente:', { duration: 5000 });
        toast.info(fanPageUrl, { duration: 10000 });
      }
    }
  };

  const formatCurrency = (value) => {
    // Formatear con separador de miles con punto
    const formatted = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `$${formatted}`;
  };

  const nombresMesesCompletos = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const nombresMesesCortos = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const mesActualNum = new Date().getMonth() + 1; // 1-12
  const mesActualStr = String(mesActualNum).padStart(2, '0');
  
  // Mes anterior
  const dAnt = new Date();
  dAnt.setMonth(dAnt.getMonth() - 1);
  const mesAnteriorStr = String(dAnt.getMonth() + 1).padStart(2, '0');

  const datosEjercicio = useMemo(() => {
    if (!stats) {
      return {
        esAnio: false,
        titulo: 'Cargando...',
        etiquetaPeriodo: '',
        subtitulo: '',
        resultado: 0,
        resultado_pagado: 0,
        total_ingresos: 0,
        total_gastos: 0,
        gastos_pendientes: 0,
        margen: '0',
        ocupacion_label: 'Ocupación',
        ocupacion_valor: '0%',
        link_ingresos: `/admin/ingresos?anio=${year}`,
        link_gastos: `/admin/gastos?anio=${year}`
      };
    }

    if (periodoEjercicio === 'anio') {
      const res = stats.resultado !== undefined ? stats.resultado : (stats.total_ingresos - stats.total_gastos);
      const resPagado = stats.resultado_pagado !== undefined ? stats.resultado_pagado : (stats.total_ingresos - stats.total_gastos);
      const totalIng = stats.total_ingresos || 0;
      const margen = totalIng > 0 ? ((res / totalIng) * 100).toFixed(1) : 0;
      return {
        esAnio: true,
        mesNum: null,
        titulo: res >= 0 ? `Utilidad Anual ${year}` : `Pérdida Anual ${year}`,
        etiquetaPeriodo: `Año ${year}`,
        subtitulo: `Balance financiero del año ${year} (considera gastos pendientes)`,
        resultado: res,
        resultado_pagado: resPagado,
        total_ingresos: totalIng,
        total_gastos: stats.total_gastos || 0,
        gastos_pendientes: stats.total_gastos_pendientes || 0,
        margen: margen,
        ocupacion_label: 'Ocupación Anual',
        ocupacion_valor: `${stats.ocupacion_porcentaje || 0}%`,
        link_ingresos: `/admin/ingresos?anio=${year}`,
        link_gastos: `/admin/gastos?anio=${year}`
      };
    }

    // Período mensual
    let mesCod = mesActualStr;
    if (periodoEjercicio === 'mes_actual') {
      mesCod = mesActualStr;
    } else if (periodoEjercicio === 'mes_anterior') {
      mesCod = mesAnteriorStr;
    } else {
      mesCod = periodoEjercicio;
    }

    const numMes = parseInt(mesCod, 10);
    const nombreMes = (numMes >= 1 && numMes <= 12) ? nombresMesesCompletos[numMes - 1] : `Mes ${mesCod}`;
    const mesKey = `${year}-${mesCod}`;

    const ingresosMes = (stats.ingresos_por_mes && stats.ingresos_por_mes[mesKey]) || 0;
    const gastosPagadosMes = (stats.gastos_por_mes && stats.gastos_por_mes[mesKey]) || 0;
    const gastosPendientesMes = (stats.gastos_pendientes_por_mes && stats.gastos_pendientes_por_mes[mesKey]) || 0;
    const totalGastosMes = gastosPagadosMes + gastosPendientesMes;
    const resultadoNetoMes = ingresosMes - totalGastosMes;
    const resultadoPagadoMes = ingresosMes - gastosPagadosMes;

    const margenMes = ingresosMes > 0 ? ((resultadoNetoMes / ingresosMes) * 100).toFixed(1) : 0;

    // Ocupación mensual
    const nochesMes = (stats.ocupacion_mensual && stats.ocupacion_mensual[mesKey]) || 0;
    const diasEnMes = new Date(year, numMes, 0).getDate();
    const ocupacionMesPorcentaje = diasEnMes > 0 ? ((nochesMes / diasEnMes) * 100).toFixed(1) : 0;

    let etiqueta = nombreMes;
    if (periodoEjercicio === 'mes_actual') etiqueta = `Mes actual (${nombreMes})`;
    if (periodoEjercicio === 'mes_anterior') etiqueta = `Mes anterior (${nombreMes})`;

    return {
      esAnio: false,
      mesNum: mesCod,
      titulo: resultadoNetoMes >= 0 ? `Utilidad ${nombreMes} ${year}` : `Pérdida ${nombreMes} ${year}`,
      etiquetaPeriodo: etiqueta,
      subtitulo: `Balance financiero de ${nombreMes} ${year} (considera gastos pendientes)`,
      resultado: resultadoNetoMes,
      resultado_pagado: resultadoPagadoMes,
      total_ingresos: ingresosMes,
      total_gastos: gastosPagadosMes,
      gastos_pendientes: gastosPendientesMes,
      margen: margenMes,
      ocupacion_label: `Ocupación ${nombreMes}`,
      ocupacion_valor: `${ocupacionMesPorcentaje}% (${nochesMes} ${nochesMes === 1 ? 'noche' : 'noches'})`,
      link_ingresos: `/admin/ingresos?anio=${year}&mes=${mesCod}`,
      link_gastos: `/admin/gastos?anio=${year}&mes=${mesCod}`
    };
  }, [stats, periodoEjercicio, year, mesActualStr, mesAnteriorStr]);

  const prepararDatosIngresoGasto = () => {
    if (!stats) return [];
    const meses = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    return meses.map(mes => {
      const mesKey = `${year}-${mes}`;
      const ingresos = (stats.ingresos_por_mes && stats.ingresos_por_mes[mesKey]) || 0;
      const gastosPagados = (stats.gastos_por_mes && stats.gastos_por_mes[mesKey]) || 0;
      const gastosPendientes = (stats.gastos_pendientes_por_mes && stats.gastos_pendientes_por_mes[mesKey]) || 0;
      const totalGastosMes = gastosPagados + gastosPendientes;
      return {
        mes: mes,
        Ingresos: ingresos,
        'Gastos Pagados': gastosPagados,
        'Gastos Pendientes': gastosPendientes,
        'Total Gastos': totalGastosMes,
        Resultado: ingresos - totalGastosMes,
        'Resultado Sólo Pagado': ingresos - gastosPagados
      };
    });
  };

  const CustomTooltipIngresoGasto = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload || {};
      const tienePendientes = (data['Gastos Pendientes'] || 0) > 0;
      const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const numMes = parseInt(label, 10);
      const nombreMes = (numMes >= 1 && numMes <= 12) ? nombresMeses[numMes - 1] : label;

      return (
        <div className="bg-white p-3 rounded-lg border border-[#8FE0FF] shadow-lg text-xs min-w-[210px]">
          <div className="font-bold text-[#0E3A53] mb-2 pb-1 border-b border-gray-100 flex items-center justify-between">
            <span>{nombreMes} {year}</span>
            {tienePendientes && (
              <span className="text-[10px] bg-amber-100 text-amber-800 font-semibold px-1.5 py-0.5 rounded">
                Con pendientes
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-[#1AA1D8]">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#1AA1D8]"></span>
                Ingresos:
              </span>
              <span className="font-semibold">{formatCurrency(data.Ingresos || 0)}</span>
            </div>
            <div className="flex justify-between items-center text-[#FF7F6A]">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#FF7F6A]"></span>
                Gastos Pagados:
              </span>
              <span className="font-semibold">{formatCurrency(data['Gastos Pagados'] || 0)}</span>
            </div>
            {tienePendientes && (
              <div className="flex justify-between items-center text-amber-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-xs bg-[#F59E0B]"></span>
                  Gastos Pendientes:
                </span>
                <span className="font-semibold">{formatCurrency(data['Gastos Pendientes'] || 0)}</span>
              </div>
            )}
            {tienePendientes && (
              <div className="flex justify-between items-center text-gray-500 pt-1 border-t border-gray-100 text-[11px]">
                <span>Total Gastos Mes:</span>
                <span className="font-semibold">{formatCurrency(data['Total Gastos'] || 0)}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-[#0E3A53] pt-1.5 border-t border-gray-200 font-bold">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-0.5 bg-[#0E3A53]"></span>
                Resultado Neto:
              </span>
              <span className={(data.Resultado || 0) >= 0 ? 'text-[#1AA1D8]' : 'text-[#FF7F6A]'}>
                {formatCurrency(data.Resultado || 0)}
              </span>
            </div>
            {tienePendientes && (
              <div className="flex justify-between items-center text-gray-400 text-[10px] italic">
                <span>(Sin pendientes: {formatCurrency(data['Resultado Sólo Pagado'] || 0)})</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const prepararDatosGastoCategoria = () => {
    if (!stats) return [];
    
    // Si se selecciona un mes específico, usar gastos_por_categoria_mes
    if (mesGastosCategoria !== 'todos' && stats.gastos_por_categoria_mes) {
      const mesKey = `${year}-${mesGastosCategoria}`;
      const datosMes = stats.gastos_por_categoria_mes[mesKey] || {};
      return Object.entries(datosMes).map(([nombre, valor]) => {
        const catObj = categorias.find(c => (c.nombre || '').trim().toLowerCase() === (nombre || '').trim().toLowerCase());
        const limiteMensual = (catObj?.gasto_mensual_maximo || {})[propiedadSeleccionada?.id_propiedad] || 0;
        const superaLimite = limiteMensual > 0 && valor > limiteMensual;
        const exceso = superaLimite ? valor - limiteMensual : 0;
        return {
          name: nombre,
          value: valor,
          limiteMensual,
          superaLimite,
          exceso,
          categoriaId: catObj?.id_categoria
        };
      });
    }
    
    // Si es 'todos', usar el total anual
    return Object.entries(stats.gastos_por_categoria || {}).map(([nombre, valor]) => {
      const catObj = categorias.find(c => (c.nombre || '').trim().toLowerCase() === (nombre || '').trim().toLowerCase());
      const limiteMensual = (catObj?.gasto_mensual_maximo || {})[propiedadSeleccionada?.id_propiedad] || 0;
      
      let mesesSuperados = [];
      if (limiteMensual > 0 && stats.gastos_por_categoria_mes) {
        Object.entries(stats.gastos_por_categoria_mes).forEach(([mesKey, mesData]) => {
          if (mesKey.startsWith(`${year}-`)) {
            const gastoMes = mesData[nombre] || 0;
            if (gastoMes > limiteMensual) {
              const numMes = mesKey.split('-')[1];
              const nomMes = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][parseInt(numMes) - 1] || numMes;
              mesesSuperados.push({ mes: nomMes, gasto: gastoMes, exceso: gastoMes - limiteMensual });
            }
          }
        });
      }
      const superaLimite = mesesSuperados.length > 0;
      return {
        name: nombre,
        value: valor,
        limiteMensual,
        superaLimite,
        mesesSuperados,
        categoriaId: catObj?.id_categoria
      };
    });
  };

  const prepararDatosOcupacion = () => {
    if (!stats) return [];
    const meses = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    const nombresCortos = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return meses.map((mes, index) => ({
      mes: nombresCortos[index],
      mesNumero: mes,
      noches: stats.ocupacion_mensual[`${year}-${mes}`] || 0
    }));
  };

  const handleOcupacionDotClick = (payload) => {
    if (payload && payload.mesNumero) {
      const mesNumero = payload.mesNumero;
      const fechaInicio = `${year}-${mesNumero}-01`;
      const ultimoDia = new Date(year, parseInt(mesNumero), 0).getDate();
      const fechaFin = `${year}-${mesNumero}-${ultimoDia}`;
      navigate(`/admin/reservas?desde=${fechaInicio}&hasta=${fechaFin}`);
    }
  };

  const handleCategoriaClick = (categoriaNombre) => {
    if (!categoriaNombre) return;
    const catObj = categorias.find(c => c.nombre.trim().toLowerCase() === categoriaNombre.trim().toLowerCase());
    const categoriaId = catObj ? catObj.id_categoria : categoriaNombre;

    const params = new URLSearchParams();
    if (propiedadSeleccionada?.id_propiedad) {
      params.set('propiedad_id', propiedadSeleccionada.id_propiedad);
    }
    if (year) params.set('anio', year.toString());
    if (mesGastosCategoria && mesGastosCategoria !== 'todos') {
      params.set('mes', mesGastosCategoria);
    } else {
      params.set('mes', 'todos');
    }
    params.set('categoria_id', categoriaId);

    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const url = `${publicUrl}/admin/gastos?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const COLORS = ['#1AA1D8', '#FF7F6A', '#8FE0FF', '#F7E8C9', '#0E3A53'];

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-xl font-semibold text-[#0E3A53]">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in-up">
      {/* Header con imagen de playa - Responsive - Cambia según propiedad */}
      <div 
        className="relative h-32 sm:h-40 md:h-48 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl"
        style={{
          backgroundImage: `url(${propiedadSeleccionada?.fotos && propiedadSeleccionada.fotos.length > 0 
            ? `${BACKEND_URL}${propiedadSeleccionada.fotos[0]}`
            : 'https://customer-assets.emergentagent.com/job_playa-morrillos/artifacts/bmc4xwlq_Captura%20de%20pantalla%202025-12-05%20a%20la%28s%29%2011.56.35.png'
          })`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1AA1D8]/30 to-[#0E3A53]/20">
          <div className="h-full flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 md:px-8 py-4 sm:py-0">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Dashboard Anual
              </h1>
              <p className="text-white/90 text-sm sm:text-base md:text-lg">Resumen completo de {year}</p>
            </div>
            <div className="flex gap-2 sm:gap-4 items-center mt-2 sm:mt-0">
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (fanPageUrl) {
                      window.open(fanPageUrl, '_blank');
                    } else {
                      toast.error('Selecciona una propiedad primero');
                    }
                  }}
                  disabled={!propiedadSeleccionada?.id_propiedad}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-white/90 backdrop-blur-md text-[#0E3A53] text-xs sm:text-sm font-medium shadow-lg border-2 border-white/20 hover:bg-white hover:shadow-xl transition-all flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Abrir Fan Page en nueva pestaña"
                >
                  <LinkIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Fan Page</span>
                </Button>
                <Button
                  onClick={copiarLinkFanPage}
                  disabled={!propiedadSeleccionada?.id_propiedad}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-white/90 backdrop-blur-md text-[#0E3A53] font-medium shadow-lg border-2 border-white/20 hover:bg-white hover:shadow-xl transition-all flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Copiar link de Fan Page"
                >
                  {linkCopiado ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <select
                data-testid="select-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-white/90 backdrop-blur-md text-[#0E3A53] text-xs sm:text-sm font-medium shadow-lg border-2 border-white/20 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
                <option value={2028}>2028</option>
                <option value={2029}>2029</option>
                <option value={2030}>2030</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Notificaciones de Solicitudes Pendientes */}
      {solicitudesPendientes.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-orange-500 rounded-2xl p-6 shadow-lg animate-pulse-slow">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon className="w-6 h-6 text-orange-600" />
                <h3 className="text-xl font-bold text-orange-900">
                  {solicitudesPendientes.length} {solicitudesPendientes.length === 1 ? 'Solicitud Pendiente' : 'Solicitudes Pendientes'}
                </h3>
              </div>
              <div className="space-y-2">
                {solicitudesPendientes.slice(0, 3).map((solicitud) => {
                  const fechaSolicitud = new Date(solicitud.fecha_solicitud);
                  const horasTranscurridas = Math.floor((new Date() - fechaSolicitud) / (1000 * 60 * 60));
                  const horasRestantes = 72 - horasTranscurridas;
                  
                  return (
                    <div key={solicitud.id_solicitud} className="bg-white/80 rounded-xl p-4 border border-orange-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-[#0E3A53]">
                            {solicitud.nombre_cliente} {solicitud.apellido_cliente || ''}
                          </p>
                          <p className="text-sm text-gray-600">
                            {solicitud.propiedad_nombre} • {new Date(solicitud.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL')} - {new Date(solicitud.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL')}
                          </p>
                          {solicitud.num_personas && (
                            <p className="text-sm text-gray-500">{solicitud.num_personas} personas</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-orange-600">
                            ⏱️ {horasRestantes}h restantes
                          </p>
                          <p className="text-xs text-gray-500">
                            {fechaSolicitud.toLocaleDateString('es-CL')} {fechaSolicitud.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {solicitudesPendientes.length > 3 && (
                <p className="text-sm text-orange-700 mt-2">
                  +{solicitudesPendientes.length - 3} solicitudes más
                </p>
              )}
            </div>
            <Button
              onClick={() => navigate('/solicitudes-reserva')}
              className="ml-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 shadow-lg transition-all"
            >
              Ver Todas
            </Button>
          </div>
        </div>
      )}

      {/* Alerta de Gastos Pendientes Próximos */}
      {gastosPendientes.length > 0 && (
        <div className="card-beach border-l-4 border-[#FF7F6A]">
          <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <AlertTriangle className="w-5 h-5 text-[#FF7F6A]" />
            ⚠️ Gastos Pendientes por Vencer
          </h3>
          <div className="space-y-3">
            {gastosPendientes.map((gasto) => {
              const fechaVencimiento = new Date(gasto.fecha + 'T12:00:00');
              const hoy = new Date();
              hoy.setHours(0,0,0,0);
              const diasRestantes = Math.ceil((fechaVencimiento - hoy) / (1000 * 60 * 60 * 24));
              const estaVencido = diasRestantes < 0;
              
              return (
                <div 
                  key={gasto.id_gasto}
                  className={`p-4 rounded-xl ${estaVencido ? 'bg-red-100 border border-red-300' : 'bg-gradient-to-r from-[#FF7F6A]/20 to-[#F7E8C9]/30'}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${estaVencido ? 'bg-red-500' : 'bg-[#FF7F6A]'}`}>
                        <RefreshCw className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0E3A53]">{gasto.proveedor}</p>
                        <p className="text-sm text-[#0E3A53]/70">
                          {obtenerNombreCategoria(gasto.categoria_id)}
                          {gasto.notas && <span className="ml-2">• {gasto.notas}</span>}
                        </p>
                        <p className={`text-xs font-medium ${estaVencido ? 'text-red-600' : 'text-[#FF7F6A]'}`}>
                          {estaVencido 
                            ? `Vencido hace ${Math.abs(diasRestantes)} día(s)` 
                            : diasRestantes === 0 
                              ? 'Vence HOY' 
                              : `Vence en ${diasRestantes} día(s)`}
                          <span className="text-[#0E3A53]/50 ml-2">
                            ({fechaVencimiento.toLocaleDateString('es-CL')})
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="text-xs text-[#0E3A53]/50">Monto</p>
                        <Input
                          type="number"
                          value={montoEditando[gasto.id_gasto] !== undefined ? montoEditando[gasto.id_gasto] : gasto.monto}
                          onChange={(e) => setMontoEditando({...montoEditando, [gasto.id_gasto]: e.target.value})}
                          className="w-28 text-right font-bold text-[#FF7F6A] h-8"
                        />
                      </div>
                      <Button
                        onClick={() => marcarGastoPagado(gasto)}
                        size="sm"
                        className="bg-[#1AA1D8] hover:bg-[#1AA1D8]/90 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Pagado
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#0E3A53]/50 mt-3 text-center">
            Puedes editar el monto antes de marcar como pagado
          </p>
        </div>
      )}

      {/* Huéspedes Actualmente Hospedados */}
      {huespedesActuales.length > 0 && (
        <div className="card-beach border-l-4 border-green-500">
          <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <Home className="w-5 h-5 text-green-500" />
            🏠 Huéspedes Actualmente Hospedados
          </h3>
          <div className="space-y-3">
            {huespedesActuales.map((reserva) => (
              <div 
                key={reserva.id_reserva}
                className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-green-100/50 border border-green-200"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-full">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#0E3A53]">{reserva.huesped_nombre || 'Huésped'}</p>
                    <p className="text-sm text-[#0E3A53]/70">
                      Check-out: {new Date(reserva.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                    DISFRUTANDO
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Check-ins de Hoy/Mañana */}
      {checkInsHoy.length > 0 && (
        <div className="card-beach border-l-4 border-[#1AA1D8]">
          <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <LogIn className="w-5 h-5 text-[#1AA1D8]" />
            🚪 Próximas Entradas (Check-in)
          </h3>
          <div className="space-y-3">
            {checkInsHoy.map((reserva) => {
              const fechaCheckIn = new Date(reserva.fecha_check_in + 'T12:00:00');
              const hoy = new Date();
              hoy.setHours(0,0,0,0);
              const esHoy = reserva.fecha_check_in === hoy.toISOString().split('T')[0];
              
              return (
                <div 
                  key={reserva.id_reserva}
                  className={`p-4 rounded-xl ${esHoy ? 'bg-[#1AA1D8]/20 border-2 border-[#1AA1D8]' : 'bg-[#8FE0FF]/20 border border-[#8FE0FF]'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${esHoy ? 'bg-[#1AA1D8]' : 'bg-[#8FE0FF]'} rounded-full`}>
                      <LogIn className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-[#0E3A53]">{reserva.huesped_nombre || 'Huésped'}</p>
                      <p className="text-sm text-[#0E3A53]/70">
                        {fechaCheckIn.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'short' })}
                        {reserva.notas && <span className="ml-2 text-xs">• {reserva.notas.substring(0, 50)}...</span>}
                      </p>
                    </div>
                    <span className={`px-3 py-1 ${esHoy ? 'bg-[#1AA1D8] text-white' : 'bg-[#8FE0FF] text-[#0E3A53]'} text-xs font-bold rounded-full`}>
                      {esHoy ? '¡HOY!' : 'MAÑANA'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Check-outs de Hoy/Mañana */}
      {checkOutsHoy.length > 0 && (
        <div className="card-beach border-l-4 border-[#FF7F6A]">
          <h3 className="text-lg font-bold text-[#0E3A53] mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <LogOut className="w-5 h-5 text-[#FF7F6A]" />
            👋 Próximas Salidas (Check-out)
          </h3>
          <div className="space-y-3">
            {checkOutsHoy.map((reserva) => {
              const fechaCheckOut = new Date(reserva.fecha_check_out + 'T12:00:00');
              const hoy = new Date();
              hoy.setHours(0,0,0,0);
              const esHoy = reserva.fecha_check_out === hoy.toISOString().split('T')[0];
              
              return (
                <div 
                  key={reserva.id_reserva}
                  className={`p-4 rounded-xl ${esHoy ? 'bg-[#FF7F6A]/20 border-2 border-[#FF7F6A]' : 'bg-[#F7E8C9]/30 border border-[#F7E8C9]'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${esHoy ? 'bg-[#FF7F6A]' : 'bg-[#F7E8C9]'} rounded-full`}>
                      <LogOut className={`w-5 h-5 ${esHoy ? 'text-white' : 'text-[#0E3A53]'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-[#0E3A53]">{reserva.huesped_nombre || 'Huésped'}</p>
                      <p className="text-sm text-[#0E3A53]/70">
                        {fechaCheckOut.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 ${esHoy ? 'bg-[#FF7F6A] text-white' : 'bg-[#F7E8C9] text-[#0E3A53]'} text-xs font-bold rounded-full`}>
                      {esHoy ? '¡HOY!' : 'MAÑANA'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Alerta de Próximas Reservas */}
      {proximasReservas.length > 0 && (
        <div className="card-beach">
          <h3 className="text-lg font-bold text-[#0E3A53] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            📅 Próximos Arriendos
          </h3>
          <div className="space-y-3">
            {proximasReservas.map((reserva, index) => (
              <div 
                key={reserva.id_reserva}
                className={`p-4 rounded-xl cursor-pointer hover:shadow-lg transition-all ${
                  index === 0 
                    ? 'bg-gradient-to-r from-[#8FE0FF]/40 to-[#F7E8C9]/40 border-l-4 border-[#1AA1D8]' 
                    : 'bg-[#8FE0FF]/10 border-l-4 border-[#8FE0FF]'
                }`}
                data-testid={`proxima-reserva-${index}`}
                onClick={() => navigate(`/reservas?reserva=${reserva.id_reserva}`)}
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${index === 0 ? 'bg-[#1AA1D8]' : 'bg-[#8FE0FF]'}`}>
                      <CalendarIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0E3A53]">{reserva.huesped_nombre}</p>
                      <p className="text-sm text-[#0E3A53]/70">
                        {new Date(reserva.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })} 
                        {' → '}
                        {new Date(reserva.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                        <span className="ml-2 text-[#1AA1D8] font-medium">({reserva.noches} {reserva.noches === 1 ? 'día' : 'días'})</span>
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={(e) => enviarWhatsAppCliente(e, reserva)}
                    size="sm"
                    className="bg-[#25D366] hover:bg-[#25D366]/90 text-white"
                    data-testid={`btn-whatsapp-${index}`}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    WhatsApp
                  </Button>
                </div>
                
                {/* Nota Recordatorio Editable */}
                <div className="mt-3 pt-3 border-t border-[#0E3A53]/10" onClick={(e) => e.stopPropagation()}>
                  {editandoNota === reserva.id_reserva ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-[#0E3A53]/70">
                        <StickyNote className="w-4 h-4" />
                        <span>Nota recordatorio:</span>
                      </div>
                      <Textarea
                        value={notaTemporal}
                        onChange={(e) => setNotaTemporal(e.target.value)}
                        placeholder="Escribe una nota recordatorio..."
                        className="text-sm min-h-[60px] bg-white/80"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelarEdicionNota}
                          className="h-8"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => guardarNota(e, reserva.id_reserva)}
                          className="h-8 bg-[#1AA1D8] hover:bg-[#1AA1D8]/90"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="flex items-start gap-2 group cursor-pointer hover:bg-white/30 p-2 rounded-lg -m-2 transition-colors"
                      onClick={(e) => iniciarEdicionNota(e, reserva)}
                    >
                      <StickyNote className="w-4 h-4 text-[#0E3A53]/50 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        {reserva.notas ? (
                          <p className="text-sm text-[#0E3A53]/70">{reserva.notas}</p>
                        ) : (
                          <p className="text-sm text-[#0E3A53]/40 italic">Click para agregar nota recordatorio...</p>
                        )}
                      </div>
                      <Edit3 className="w-4 h-4 text-[#1AA1D8] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumen de Utilidad/Pérdida (Ejercicio del Mes / Año) */}
      <div className="card-beach">
        {/* Barra superior con selector de período y accesos rápidos */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 mb-4 pb-3 border-b border-[#8FE0FF]/30">
          <div className="flex items-center gap-2 text-xs text-[#0E3A53] font-semibold">
            <CalendarIcon className="w-4 h-4 text-[#1AA1D8]" />
            <span>Ejercicio:</span>
            <span className="text-[11px] font-bold text-[#1AA1D8] bg-[#1AA1D8]/10 px-2 py-0.5 rounded-full border border-[#1AA1D8]/20">
              {datosEjercicio.etiquetaPeriodo}
            </span>
          </div>

          {/* Opciones rápidas: Mes actual, Mes anterior, Año, Selector de mes */}
          <div className="flex flex-wrap items-center gap-1.5 bg-[#8FE0FF]/15 border border-[#8FE0FF]/40 p-1 rounded-xl w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setPeriodoEjercicio('mes_actual')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                periodoEjercicio === 'mes_actual'
                  ? 'bg-[#1AA1D8] text-white shadow-xs'
                  : 'text-[#0E3A53] hover:bg-[#8FE0FF]/30'
              }`}
              title="Ver balance del mes en curso"
            >
              <span>Mes actual</span>
              <span className={`text-[10px] px-1 py-0.2 rounded font-normal ${periodoEjercicio === 'mes_actual' ? 'bg-white/20 text-white' : 'bg-[#1AA1D8]/10 text-[#1AA1D8]'}`}>
                {nombresMesesCortos[mesActualNum - 1]}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setPeriodoEjercicio('mes_anterior')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                periodoEjercicio === 'mes_anterior'
                  ? 'bg-[#1AA1D8] text-white shadow-xs'
                  : 'text-[#0E3A53] hover:bg-[#8FE0FF]/30'
              }`}
              title="Ver balance del mes anterior"
            >
              <span>Mes anterior</span>
              <span className={`text-[10px] px-1 py-0.2 rounded font-normal ${periodoEjercicio === 'mes_anterior' ? 'bg-white/20 text-white' : 'bg-[#1AA1D8]/10 text-[#1AA1D8]'}`}>
                {nombresMesesCortos[parseInt(mesAnteriorStr, 10) - 1]}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setPeriodoEjercicio('anio')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                periodoEjercicio === 'anio'
                  ? 'bg-[#1AA1D8] text-white shadow-xs'
                  : 'text-[#0E3A53] hover:bg-[#8FE0FF]/30'
              }`}
              title={`Ver balance de todo el año ${year}`}
            >
              Año {year}
            </button>

            {/* Selector desplegable de mes */}
            <select
              value={
                periodoEjercicio === 'mes_actual'
                  ? mesActualStr
                  : periodoEjercicio === 'mes_anterior'
                  ? mesAnteriorStr
                  : periodoEjercicio === 'anio'
                  ? 'todos'
                  : periodoEjercicio
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'todos') {
                  setPeriodoEjercicio('anio');
                } else if (val === mesActualStr) {
                  setPeriodoEjercicio('mes_actual');
                } else if (val === mesAnteriorStr) {
                  setPeriodoEjercicio('mes_anterior');
                } else {
                  setPeriodoEjercicio(val);
                }
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-white/90 text-[#0E3A53] border border-[#8FE0FF] focus:outline-none focus:ring-2 focus:ring-[#1AA1D8]"
              title="Cambiar a cualquier mes del año"
            >
              <option value="todos">Todo el año {year}</option>
              <option value="01">Enero {year}</option>
              <option value="02">Febrero {year}</option>
              <option value="03">Marzo {year}</option>
              <option value="04">Abril {year}</option>
              <option value="05">Mayo {year}</option>
              <option value="06">Junio {year}</option>
              <option value="07">Julio {year}</option>
              <option value="08">Agosto {year}</option>
              <option value="09">Septiembre {year}</option>
              <option value="10">Octubre {year}</option>
              <option value="11">Noviembre {year}</option>
              <option value="12">Diciembre {year}</option>
            </select>
          </div>
        </div>

        {/* Encabezado con título e importe principal */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className={`p-3 sm:p-4 rounded-full ${datosEjercicio.resultado >= 0 ? 'bg-[#1AA1D8]' : 'bg-[#FF7F6A]'} shrink-0`}>
              {datosEjercicio.resultado >= 0 ? (
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              ) : (
                <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {datosEjercicio.titulo}
              </h2>
              <p className="text-xs sm:text-sm text-[#0E3A53]/70">
                {datosEjercicio.subtitulo}
              </p>
            </div>
          </div>
          <div className="w-full sm:w-auto text-left sm:text-right pt-2 sm:pt-0 border-t sm:border-0 border-[#8FE0FF]/30">
            <p className={`text-2xl sm:text-3xl md:text-4xl font-bold ${datosEjercicio.resultado >= 0 ? 'text-[#1AA1D8]' : 'text-[#FF7F6A]'}`}>
              {formatCurrency(datosEjercicio.resultado)}
            </p>
            {datosEjercicio.resultado_pagado !== undefined && datosEjercicio.gastos_pendientes > 0 && (
              <p className="text-xs text-[#0E3A53]/60 mt-0.5">
                Sólo pagados: <span className="font-semibold">{formatCurrency(datosEjercicio.resultado_pagado)}</span>
              </p>
            )}
          </div>
        </div>

        {/* Grilla de 4 KPIs dinámicos del período */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 pt-4 border-t-2 border-[#8FE0FF]">
          <div 
            className="text-center cursor-pointer hover:bg-[#1AA1D8]/10 p-1.5 sm:p-2 rounded-lg transition-colors"
            onClick={() => navigate(datosEjercicio.link_ingresos)}
            title="Ver ingresos del período"
          >
            <p className="text-xs sm:text-sm text-[#0E3A53]/60 mb-0.5 sm:mb-1">Total Ingresos</p>
            <p className="text-base sm:text-xl font-semibold text-[#1AA1D8] hover:underline">
              {formatCurrency(datosEjercicio.total_ingresos)}
            </p>
          </div>
          <div 
            className="text-center cursor-pointer hover:bg-[#FF7F6A]/10 p-1.5 sm:p-2 rounded-lg transition-colors"
            onClick={() => navigate(datosEjercicio.link_gastos)}
            title="Ver gastos del período"
          >
            <p className="text-xs sm:text-sm text-[#0E3A53]/60 mb-0.5 sm:mb-1">Total Gastos</p>
            <p className="text-base sm:text-xl font-semibold text-[#FF7F6A] hover:underline">
              {formatCurrency(datosEjercicio.total_gastos)}
            </p>
            {datosEjercicio.gastos_pendientes > 0 && (
              <p className="text-[10px] text-amber-600 font-medium">
                +{formatCurrency(datosEjercicio.gastos_pendientes)} pendientes
              </p>
            )}
          </div>
          <div className="text-center p-1.5 sm:p-2">
            <p className="text-xs sm:text-sm text-[#0E3A53]/60 mb-0.5 sm:mb-1">Margen</p>
            <p className="text-base sm:text-xl font-semibold text-[#0E3A53]">
              {datosEjercicio.margen}%
            </p>
          </div>
          <div className="text-center p-1.5 sm:p-2">
            <p className="text-xs sm:text-sm text-[#0E3A53]/60 mb-0.5 sm:mb-1">
              {datosEjercicio.ocupacion_label}
            </p>
            <p className="text-base sm:text-xl font-semibold text-[#1AA1D8]">
              {datosEjercicio.ocupacion_valor}
            </p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingresos vs Gastos */}
        <div className="card-beach">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <div>
              <h2 className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Ingresos vs Gastos Mensual
              </h2>
              {stats.total_gastos_pendientes > 0 && (
                <p className="text-xs text-amber-700 font-medium mt-0.5">
                  Gastos pendientes del año: <strong>{formatCurrency(stats.total_gastos_pendientes)}</strong>
                </p>
              )}
            </div>

            {/* Toggle Apilado / Separado */}
            <div className="flex items-center gap-1 bg-[#8FE0FF]/20 border border-[#8FE0FF]/50 p-0.5 rounded-lg text-xs self-start sm:self-auto">
              <span className="text-[11px] text-[#0E3A53]/70 px-1 font-medium hidden sm:inline">Gastos:</span>
              <button
                type="button"
                onClick={() => setVistaGastosGrafico('apilado')}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                  vistaGastosGrafico === 'apilado'
                    ? 'bg-[#1AA1D8] text-white shadow-xs'
                    : 'text-[#0E3A53] hover:bg-[#8FE0FF]/30'
                }`}
                title="Muestra los gastos pagados y pendientes apilados en una sola columna de gasto mensual"
              >
                Apilados
              </button>
              <button
                type="button"
                onClick={() => setVistaGastosGrafico('separado')}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                  vistaGastosGrafico === 'separado'
                    ? 'bg-[#1AA1D8] text-white shadow-xs'
                    : 'text-[#0E3A53] hover:bg-[#8FE0FF]/30'
                }`}
                title="Muestra barras separadas para Gastos Pagados y Gastos Pendientes"
              >
                Separados
              </button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={prepararDatosIngresoGasto()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#8FE0FF" />
              <XAxis dataKey="mes" stroke="#0E3A53" />
              <YAxis stroke="#0E3A53" />
              <Tooltip content={<CustomTooltipIngresoGasto />} />
              <Legend />
              <ReferenceLine y={0} stroke="#0E3A53" strokeDasharray="3 3" />
              <Bar dataKey="Ingresos" fill="#1AA1D8" radius={[8, 8, 0, 0]} />
              <Bar 
                dataKey="Gastos Pagados" 
                stackId={vistaGastosGrafico === 'apilado' ? 'gastos' : undefined} 
                fill="#FF7F6A" 
                radius={vistaGastosGrafico === 'apilado' ? [0, 0, 0, 0] : [8, 8, 0, 0]} 
              />
              <Bar 
                dataKey="Gastos Pendientes" 
                stackId={vistaGastosGrafico === 'apilado' ? 'gastos' : undefined} 
                fill="#F59E0B" 
                radius={[8, 8, 0, 0]} 
              />
              <Line 
                type="monotone" 
                dataKey="Resultado" 
                stroke="#0E3A53" 
                strokeWidth={3}
                dot={{ fill: '#0E3A53', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#0E3A53' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Distribución de Gastos */}
        <div className="card-beach">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <h2 className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Distribución de Gastos
            </h2>
            <select
              value={mesGastosCategoria}
              onChange={(e) => setMesGastosCategoria(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-[#8FE0FF]/20 text-[#0E3A53] text-sm font-medium border border-[#8FE0FF] focus:outline-none focus:ring-2 focus:ring-[#1AA1D8]"
            >
              <option value="todos">Todo el año {year}</option>
              <option value="01">Enero {year}</option>
              <option value="02">Febrero {year}</option>
              <option value="03">Marzo {year}</option>
              <option value="04">Abril {year}</option>
              <option value="05">Mayo {year}</option>
              <option value="06">Junio {year}</option>
              <option value="07">Julio {year}</option>
              <option value="08">Agosto {year}</option>
              <option value="09">Septiembre {year}</option>
              <option value="10">Octubre {year}</option>
              <option value="11">Noviembre {year}</option>
              <option value="12">Diciembre {year}</option>
            </select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Torta */}
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={prepararDatosGastoCategoria()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    cursor="pointer"
                    onClick={(entry) => {
                      if (entry && entry.name) {
                        handleCategoriaClick(entry.name);
                      }
                    }}
                  >
                    {prepararDatosGastoCategoria().map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={COLORS[index % COLORS.length]} 
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0].payload;
                        return (
                          <div className="bg-white p-2.5 rounded-lg shadow-lg border border-[#8FE0FF] text-xs">
                            <p className="font-bold text-[#0E3A53]">{item.name}</p>
                            <p className="text-[#FF7F6A] font-semibold">{formatCurrency(item.value)}</p>
                            {item.superaLimite && (
                              <div className="flex items-center gap-1 text-red-600 font-bold mt-1.5 pt-1.5 border-t border-red-100">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                <span>
                                  {mesGastosCategoria !== 'todos'
                                    ? `Supera estimado (${formatCurrency(item.limiteMensual)}) por +${formatCurrency(item.exceso)}`
                                    : `Superó el estimado mensual (${formatCurrency(item.limiteMensual)}) en ${item.mesesSuperados?.length} mes(es)`
                                  }
                                </span>
                              </div>
                            )}
                            <p className="text-[10px] text-[#1AA1D8] mt-1 font-medium">
                              Click para abrir gastos en nueva pestaña ↗
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Top 5 de Gastos por Categoría */}
            <div className="flex flex-col justify-center">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Top 5 Categorías
                </h3>
                <span className="text-xs text-[#0E3A53]/50">Click para abrir en nueva pestaña</span>
              </div>
              <div className="space-y-3">
                {prepararDatosGastoCategoria()
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 5)
                  .map((item, index) => (
                    <div 
                      key={index}
                      onClick={() => handleCategoriaClick(item.name)}
                      className={`flex items-center justify-between p-3 rounded-lg hover:bg-[#8FE0FF]/20 transition-all cursor-pointer group border ${
                        item.superaLimite ? 'bg-red-50/40 border-red-200' : 'border-transparent hover:border-[#8FE0FF]/40'
                      }`}
                      title={
                        item.superaLimite
                          ? (mesGastosCategoria !== 'todos' 
                              ? `⚠️ Supera el valor de gasto mensual máximo estimado de ${formatCurrency(item.limiteMensual)} (+${formatCurrency(item.exceso)}) — Clic para abrir en nueva pestaña`
                              : `⚠️ Superó el gasto mensual máximo estimado (${formatCurrency(item.limiteMensual)}) en: ${item.mesesSuperados?.map(m => m.mes).join(', ')} — Clic para abrir en nueva pestaña`)
                          : `Ver gastos de ${item.name} en nueva pestaña (${mesGastosCategoria === 'todos' ? year : `${mesGastosCategoria}/${year}`}) ↗`
                      }
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div 
                          className="w-4 h-4 rounded-full transition-transform group-hover:scale-125 shrink-0"
                          style={{ backgroundColor: COLORS[prepararDatosGastoCategoria().findIndex(cat => cat.name === item.name) % COLORS.length] }}
                        ></div>
                        <span className="font-medium text-[#0E3A53] group-hover:text-[#1AA1D8] transition-colors truncate">{item.name}</span>
                        {item.superaLimite && (
                          <AlertTriangle 
                            className="w-4 h-4 text-red-500 shrink-0 animate-pulse" 
                            title={
                              mesGastosCategoria !== 'todos' 
                                ? `Supera el valor de gasto mensual máximo estimado de ${formatCurrency(item.limiteMensual)} (+${formatCurrency(item.exceso)})`
                                : `Superó el gasto mensual máximo estimado (${formatCurrency(item.limiteMensual)}) en: ${item.mesesSuperados?.map(m => m.mes).join(', ')}`
                            }
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-bold ${item.superaLimite ? 'text-red-600' : 'text-[#FF7F6A]'}`}>
                          {formatCurrency(item.value)}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#1AA1D8] opacity-60 group-hover:opacity-100 transition-all" />
                      </div>
                    </div>
                  ))}
                
                {/* Link para ver todas las categorías */}
                {prepararDatosGastoCategoria().length > 5 && (
                  <button
                    onClick={() => setMostrarResumenGastos(true)}
                    className="w-full mt-3 py-2 text-sm text-[#1AA1D8] hover:text-[#0E3A53] font-medium flex items-center justify-center gap-2 hover:bg-[#8FE0FF]/10 rounded-lg transition-colors"
                  >
                    Ver todas las categorías ({prepararDatosGastoCategoria().length})
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                {prepararDatosGastoCategoria().length <= 5 && prepararDatosGastoCategoria().length > 0 && (
                  <button
                    onClick={() => setMostrarResumenGastos(true)}
                    className="w-full mt-3 py-2 text-sm text-[#1AA1D8] hover:text-[#0E3A53] font-medium flex items-center justify-center gap-2 hover:bg-[#8FE0FF]/10 rounded-lg transition-colors"
                  >
                    Ver resumen completo
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ocupación Mensual */}
      <div className="card-beach">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h2 className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ocupación Mensual (Noches)
          </h2>
          <div className="flex gap-6">
            <div className="text-center px-4 py-2 bg-[#1AA1D8]/10 rounded-lg">
              <p className="text-xs text-[#0E3A53]/60 mb-1">Total Días Arrendados</p>
              <p className="text-2xl font-bold text-[#1AA1D8]">
                {Object.values(stats?.ocupacion_mensual || {}).reduce((sum, n) => sum + n, 0)}
              </p>
            </div>
            <div className="text-center px-4 py-2 bg-[#8FE0FF]/20 rounded-lg">
              <p className="text-xs text-[#0E3A53]/60 mb-1">% Ocupación Anual</p>
              <p className="text-2xl font-bold text-[#0E3A53]">
                {stats?.ocupacion_porcentaje || 0}%
              </p>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart 
            data={prepararDatosOcupacion()}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#8FE0FF" />
            <XAxis dataKey="mes" stroke="#0E3A53" />
            <YAxis stroke="#0E3A53" />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-lg border border-[#8FE0FF]">
                      <p className="font-bold text-[#0E3A53]">{payload[0].payload.mes} {year}</p>
                      <p className="text-[#1AA1D8]">{payload[0].value} noches</p>
                      <p className="text-xs text-[#0E3A53]/60 mt-1">Click para ver reservas</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="noches" 
              stroke="#1AA1D8" 
              strokeWidth={3} 
              dot={(props) => {
                const { cx, cy, payload } = props;
                return (
                  <circle
                    key={`dot-${payload?.mesNumero}`}
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill="#1AA1D8"
                    stroke="white"
                    strokeWidth={1}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (payload?.mesNumero) {
                        const mesNumero = payload.mesNumero;
                        const fechaInicio = `${year}-${mesNumero}-01`;
                        const ultimoDia = new Date(year, parseInt(mesNumero), 0).getDate();
                        const fechaFin = `${year}-${mesNumero}-${ultimoDia}`;
                        navigate(`/reservas?desde=${fechaInicio}&hasta=${fechaFin}`);
                      }
                    }}
                  />
                );
              }}
              activeDot={(props) => {
                const { cx, cy, payload } = props;
                return (
                  <circle
                    key={`active-dot-${payload?.mesNumero}`}
                    cx={cx}
                    cy={cy}
                    r={10}
                    fill="#0E3A53"
                    stroke="#1AA1D8"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (payload?.mesNumero) {
                        const mesNumero = payload.mesNumero;
                        const fechaInicio = `${year}-${mesNumero}-01`;
                        const ultimoDia = new Date(year, parseInt(mesNumero), 0).getDate();
                        const fechaFin = `${year}-${mesNumero}-${ultimoDia}`;
                        navigate(`/reservas?desde=${fechaInicio}&hasta=${fechaFin}`);
                      }
                    }}
                  />
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-xs text-center text-[#0E3A53]/50 mt-2">
          Haz click en un punto para ver las reservas del mes
        </p>
      </div>

      {/* Modal: Resumen Completo de Gastos por Categoría */}
      <Dialog open={mostrarResumenGastos} onOpenChange={setMostrarResumenGastos}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Resumen de Gastos por Categoría
              <span className="block text-sm font-normal text-[#0E3A53]/60 mt-1">
                {mesGastosCategoria === 'todos' 
                  ? `Todo el año ${year}` 
                  : `${['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][parseInt(mesGastosCategoria) - 1]} ${year}`
                }
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-2">
              {prepararDatosGastoCategoria()
                .sort((a, b) => b.value - a.value)
                .map((item, index) => (
                  <div 
                    key={index}
                    onClick={() => {
                      setMostrarResumenGastos(false);
                      handleCategoriaClick(item.name);
                    }}
                    className={`flex items-center justify-between p-3 rounded-lg hover:bg-[#8FE0FF]/20 transition-all cursor-pointer group border ${
                      item.superaLimite ? 'bg-red-50/50 border-red-200' : 'border-transparent hover:border-[#8FE0FF]/40'
                    }`}
                    title={
                      item.superaLimite
                        ? (mesGastosCategoria !== 'todos'
                            ? `⚠️ Supera el gasto máximo mensual estimado de ${formatCurrency(item.limiteMensual)} (+${formatCurrency(item.exceso)}) — Clic para abrir en nueva pestaña`
                            : `⚠️ Superó el gasto máximo mensual estimado (${formatCurrency(item.limiteMensual)}) en: ${item.mesesSuperados?.map(m => m.mes).join(', ')} — Clic para abrir en nueva pestaña`)
                        : `Ver gastos de ${item.name} en nueva pestaña ↗`
                    }
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#0E3A53] group-hover:text-[#1AA1D8] transition-colors">{item.name}</span>
                          {item.superaLimite && (
                            <span 
                              className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded-full"
                            >
                              <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
                              <span>Excedido</span>
                            </span>
                          )}
                        </div>
                        {item.limiteMensual > 0 && (
                          <p className="text-[11px] text-[#0E3A53]/60 mt-0.5">
                            Límite mensual est: <span className="font-semibold">{formatCurrency(item.limiteMensual)}</span>
                            {item.superaLimite && mesGastosCategoria !== 'todos' && (
                              <span className="text-red-500 font-bold ml-1.5">(+{formatCurrency(item.exceso)})</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`font-bold ${item.superaLimite ? 'text-red-600' : 'text-[#FF7F6A]'}`}>
                        {formatCurrency(item.value)}
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#1AA1D8] opacity-60 group-hover:opacity-100 transition-all" />
                    </div>
                  </div>
                ))}
            </div>
            
            {/* Total */}
            <div className="mt-4 pt-4 border-t border-[#8FE0FF]/30">
              <div className="flex items-center justify-between p-3 bg-[#0E3A53]/5 rounded-lg">
                <span className="font-bold text-[#0E3A53]">Total</span>
                <span className="font-bold text-xl text-[#FF7F6A]">
                  {formatCurrency(prepararDatosGastoCategoria().reduce((sum, item) => sum + item.value, 0))}
                </span>
              </div>
            </div>
          </div>
          
          <div className="pt-4 border-t flex justify-between items-center">
            <button
              onClick={() => {
                setMostrarResumenGastos(false);
                navigate(`/admin/gastos?anio=${year}&mes=${mesGastosCategoria}`);
              }}
              className="text-sm text-[#1AA1D8] hover:text-[#0E3A53] font-medium flex items-center gap-1"
            >
              Ir a Gastos ({mesGastosCategoria === 'todos' ? `Año ${year}` : `${mesGastosCategoria}/${year}`})
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
            <Button variant="outline" onClick={() => setMostrarResumenGastos(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
